import { FormControl, InputLabel, TextField, Button } from "@mui/material";
import React, { useState } from "react";
import { v4 as uuid } from "uuid";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { Link } from "react-router-dom";
import { PersonaTypeSelect } from "./PersonaTypeSelect";
import { Persona, PersonaType } from "../../models";
import { useSnackbar } from "notistack";
import { createPersona } from "../../api";
import { PersonaCompanySelect } from "./PersonaCompanySelect";
import { useShowAllCompanies } from "../../hooks/useShowAllCompanies";

interface PersonaCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPersonaCreate: (persona: Persona) => void;
}

export const PersonaCreateDialog: React.FC<PersonaCreateDialogProps> = ({ open, onOpenChange, onPersonaCreate }) => {
  const { enqueueSnackbar } = useSnackbar();
  const user = useCurrentUser();

  const [persona, setPersona] = useState<Omit<Persona, "id" | "pageConfig" | "promptConfig" | "status">>({
    uuid: uuid(),
    masterPersona: false,
    name: "",
    type: "default",
    profileName: "",
    profilePictureUrl: "",
    languageCodeId: 1,
    processed: false,
    companyId: null,
  });

  const [showAll] = useShowAllCompanies();
  const canUnassign = Boolean(user?.perms.admin) && showAll;

  const [createdPersona, setCreatedPersona] = useState<Persona | undefined>(undefined);

  async function handlePersonaCreate() {
    enqueueSnackbar("Adding new persona", { variant: "info" });

    try {
      const created = await createPersona(persona);
      setCreatedPersona(created);
      enqueueSnackbar("Added new persona", { variant: "success" });
      onPersonaCreate(created);
    } catch (e) {
      enqueueSnackbar("Something went wrong", { variant: "error" });
      console.error(e)
    }
  }

  if (open) {
    return (
        <div className={`user-box ${open ? "" : "hide"}`}>
          <div className="user-container">
            <TextField
                className="spacer"
                variant="outlined"
                label="Name"
                name="name"
                value={persona.name}
                onChange={(e) => setPersona({ ...persona, name: e.target.value })}
            />
            <TextField
                style={{ paddingTop: "10px" }}
                variant="outlined"
                label="Profile Name"
                name="profileName"
                value={persona.profileName}
                onChange={(e) => setPersona({ ...persona, profileName: e.target.value })}
            />
            <TextField
                style={{ paddingTop: "10px" }}
                variant="outlined"
                label="Profile Picture URL"
                name="profilePictureUrl"
                value={persona.profilePictureUrl}
                onChange={(e) => setPersona({ ...persona, profilePictureUrl: e.target.value })}
            />
            <FormControl style={{ paddingTop: "10px" }} variant="outlined">
              <InputLabel>Type</InputLabel>
              <PersonaTypeSelect
                  onChange={(e) => setPersona({ ...persona, type: e.target.value as PersonaType })}
                  value={persona.type}
              />
            </FormControl>
            <PersonaCompanySelect
                style={{ paddingTop: "10px" }}
                value={persona.companyId}
                onChange={(companyId) => setPersona({ ...persona, companyId })}
            />
            <Button
                style={{ marginTop: "10px" }}
                variant="contained"
                color="primary"
                disabled={!persona.companyId && !canUnassign}
                onClick={() => handlePersonaCreate()}>
              Submit
            </Button>
            <Button
                component={Link}
                to={!user?.perms.p2 ? `/new-personas` : ` /new-personas/edit/${persona.uuid} `}
                disabled={!user?.perms.p2 || !createdPersona}
                style={{ marginTop: "10px" }}
                variant="contained"
                color="primary"
            >
              Go to Persona
            </Button>
            <Button
                style={{ marginTop: "10px" }}
                variant="contained"
                color="primary"
                onClick={() => onOpenChange?.(false)}>
              Close
            </Button>
          </div>
        </div>
    )
  } else {
    return <></>
  }
};
